// The partners the signed-in user follows — opened from the "Following"
// button on their profile. Searchable; tapping a partner opens their profile.
import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import { auth } from '../firebase';
import { useFollowedPartners } from '../hooks/useVenueVibes';
import { useI18n } from '../i18n';
import { Radius, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';
import type { RootStackParamList } from '../types';

type Props = NativeStackScreenProps<RootStackParamList, 'FollowingPartners'>;

export default function FollowingPartnersScreen({ navigation }: Props) {
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const { partners, loading } = useFollowedPartners(auth.currentUser?.uid ?? null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return partners;
    return partners.filter(partner => [partner.name, partner.category, partner.city].join(' ').toLowerCase().includes(term));
  }, [partners, search]);

  const icon = theme.isDark ? '#fff' : theme.colors.icon;
  const muted = theme.isDark ? 'rgba(255,255,255,0.5)' : theme.colors.iconMuted;

  return (
    <TonyteBackground appearanceAware>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <TouchableOpacity style={[styles.iconButton, appearance.control]} onPress={() => navigation.goBack()}>
          <Icon name="chevron-left" size={22} color={icon} />
        </TouchableOpacity>
        <Text style={[styles.title, appearance.textPrimary]}>{t('profile.following')}</Text>
      </View>
      <View style={[styles.searchBar, appearance.input]}>
        <Icon name="search" size={16} color={muted} />
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder={t('profile.followingSearch')}
          placeholderTextColor={theme.isDark ? 'rgba(255,255,255,0.42)' : theme.colors.textPlaceholder}
          style={[styles.searchInput, appearance.textPrimary]}
          autoCorrect={false}
        />
      </View>
      {loading && !partners.length ? (
        <ActivityIndicator style={styles.loader} color={theme.isDark ? '#fff' : theme.colors.accent} />
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={item => item.id}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={[styles.list, { paddingBottom: insets.bottom + 30 }]}
          ListEmptyComponent={(
            <Text style={[styles.empty, appearance.textSecondary]}>
              {partners.length ? t('profile.followingNoMatch') : t('profile.followingEmpty')}
            </Text>
          )}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[styles.row, appearance.card]}
              onPress={() => navigation.navigate('VenueProfile', { venueId: item.id })}
              activeOpacity={0.85}
            >
              <View style={[styles.logo, appearance.surface]}>
                {item.logoUrl
                  ? <Image source={{ uri: item.logoUrl }} style={styles.logoImage} />
                  : <Text style={[styles.logoLetter, appearance.textPrimary]}>{item.name.slice(0, 1).toUpperCase()}</Text>}
              </View>
              <View style={styles.copy}>
                <Text style={[styles.name, appearance.textPrimary]} numberOfLines={1}>{item.name}</Text>
                <Text style={[styles.meta, appearance.textSecondary]} numberOfLines={1}>{[item.category, item.city].filter(Boolean).join(' · ')}</Text>
              </View>
              <Icon name="chevron-right" size={18} color={muted} />
            </TouchableOpacity>
          )}
        />
      )}
    </TonyteBackground>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingBottom: 12 },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  title: { color: '#fff', fontSize: 24, fontWeight: Typography.extrabold },
  searchBar: {
    marginHorizontal: 16,
    height: 46,
    borderRadius: Radius.pill,
    paddingHorizontal: 15,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: 'rgba(255,255,255,0.09)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  searchInput: { flex: 1, color: '#fff', fontSize: 14, paddingVertical: 0 },
  loader: { marginTop: 50 },
  list: { padding: 16, gap: 10 },
  empty: { color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center', marginTop: 40, paddingHorizontal: 24 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 10,
    borderRadius: 16,
    backgroundColor: '#101722',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  logo: {
    width: 46,
    height: 46,
    borderRadius: 23,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1b2331',
  },
  logoImage: { width: '100%', height: '100%' },
  logoLetter: { color: '#fff', fontSize: 18, fontWeight: Typography.extrabold },
  copy: { flex: 1, minWidth: 0 },
  name: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold },
  meta: { color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 },
});

// Midnight stays the canonical StyleSheet above; Daylight only overrides.
const createThemeStyles = (theme: TonyteTheme) => {
  const c = theme.colors;
  return StyleSheet.create({
    control: daylightStyle(theme, { backgroundColor: c.controlBackground, borderColor: c.border }),
    input: daylightStyle(theme, { backgroundColor: c.inputBackground, borderColor: c.border }),
    card: daylightStyle(theme, { backgroundColor: c.cardBackground, borderColor: c.cardBorder, ...theme.shadows.card }),
    surface: daylightStyle(theme, { backgroundColor: c.backgroundSecondary }),
    textPrimary: daylightStyle(theme, { color: c.textPrimary }),
    textSecondary: daylightStyle(theme, { color: c.textSecondary }),
  });
};
